import { User } from '@/lib/models/client.types';
import { userService } from '@/lib/services';
import { logger } from '@/lib/utils/logger';
import { observable } from '@legendapp/state';
import { Session } from 'next-auth';

// Types
export interface UserState {
  profile: {
    data: User | null;
    isLoading: boolean;
    error: Error | null;
  };
  session: Session | null;
}

// Initial state
const initialState: UserState = {
  profile: {
    data: null,
    isLoading: false,
    error: null,
  },
  session: null,
};

// Create the observable store
export const userState = observable<UserState>(initialState);

// Action creators
export const userActions = {
  // Session management
  setSession: (session: Session | null) => {
    logger.debug('[userStore] Setting session', { session });
    userState.session.set(session);
  },

  // User actions
  setUser: (profile: User | null) => {
    logger.debug('[userStore] Setting profile', { profile });
    userState.profile.data.set(profile);
  },

  startLoading: () => {
    logger.debug('[userStore] Starting loading');
    userState.profile.isLoading.set(true);
    userState.profile.error.set(null);
  },

  setError: (error: Error) => {
    logger.debug('[userStore] Setting error', { error });
    userState.profile.isLoading.set(false);
    userState.profile.error.set(error);
  },

  completeLoading: () => {
    logger.debug('[userStore] Completing loading');
    userState.profile.isLoading.set(false);
  },

  reset: () => {
    logger.debug('[userStore] Resetting state');
    userState.set(initialState);
  },

  // Service-connected actions
  fetchUser: async () => {
    const session = userState.session.get();
    if (!session?.user?.id) return;

    try {
      userActions.startLoading();
      const profile = await userService.getUser(session.user.id, session);
      userActions.setUser(profile);
    } catch (error) {
      logger.error('[userStore] Failed to fetch profile:', error);
      userActions.setError(error as Error);
    } finally {
      userActions.completeLoading();
    }
  },

  updateUser: async (data: Partial<User>) => {
    const session = userState.session.get();
    if (!session?.user?.id) return;

    try {
      userActions.startLoading();
      const updatedUser = await userService.updateUser(
        session.user.id,
        data,
        session
      );
      userActions.setUser(updatedUser);
    } catch (error) {
      logger.error('[userStore] Failed to update profile:', error);
      userActions.setError(error as Error);
    } finally {
      userActions.completeLoading();
    }
  },

  updateLanguage: async (language: string) => {
    const session = userState.session.get();
    if (!session?.user?.id) return;

    try {
      userActions.startLoading();
      const updatedUser = await userService.updateLanguage(
        session.user.id,
        language,
        session
      );
      userActions.setUser(updatedUser);
    } catch (error) {
      logger.error('[userStore] Failed to update language:', error);
      userActions.setError(error as Error);
    } finally {
      userActions.completeLoading();
    }
  },
};

// Selectors
export const userSelectors = {
  getUser: () => userState.profile.data.get(),
  isLoading: () => userState.profile.isLoading.get(),
  getError: () => userState.profile.error.get(),
  getSession: () => userState.session.get(),
};
